// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {useMergeRefs} from '@app/features/app/hooks/useMergeRefs';
import {useReactionTooltip} from '@app/features/messaging/hooks/useReactionTooltip';
import styles from '@app/features/ui/alt_text_tooltip/AltTextTooltip.module.css';
import {EmojiTooltipContent} from '@app/features/ui/emoji_tooltip_content/EmojiTooltipContent';
import {useTooltipPortalRoot} from '@app/features/ui/tooltip/Tooltip';
import {getReducedMotionProps, TOOLTIP_MOTION} from '@app/features/ui/utils/ReducedMotionAnimation';
import {FloatingPortal} from '@floating-ui/react';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect} from 'react';

interface AltTextTooltipProps {
	altText: string;
	children: React.ReactElement<{ref?: React.Ref<HTMLElement>}>;
	onPopoutToggle?: (open: boolean) => void;
}
export const AltTextTooltip: React.FC<AltTextTooltipProps> = observer(({altText, children, onPopoutToggle}) => {
	const portalRoot = useTooltipPortalRoot();
	const tooltipMotion = getReducedMotionProps(TOOLTIP_MOTION, Accessibility.useReducedMotion);
	const {targetRef, tooltipRef, state, updatePosition, handlers, tooltipHandlers} = useReactionTooltip(200, 'top-end');
	const child = React.Children.only(children);
	const mergedRef = useMergeRefs([targetRef, child.props.ref]);
	useEffect(() => {
		if (!onPopoutToggle) {
			return;
		}
		onPopoutToggle(state.isOpen);
	}, [onPopoutToggle, state.isOpen]);
	const stopPropagation = useCallback((event: React.SyntheticEvent) => {
		event.stopPropagation();
	}, []);
	return (
		<>
			{React.cloneElement(child, {
				ref: mergedRef,
				...handlers,
			})}
			{portalRoot && (
				<FloatingPortal root={portalRoot}>
					<AnimatePresence>
						{state.isOpen && (
							<motion.div
								ref={(node: HTMLDivElement | null) => {
									(tooltipRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
									if (node && targetRef.current) {
										updatePosition();
									}
								}}
								style={{
									position: 'fixed',
									left: state.x,
									top: state.y,
									zIndex: 'var(--z-index-tooltip)',
									visibility: state.isReady ? 'visible' : 'hidden',
								}}
								{...tooltipMotion}
								{...tooltipHandlers}
								onMouseDown={stopPropagation}
								onTouchStart={stopPropagation}
								onClick={stopPropagation}
							>
								<EmojiTooltipContent
									className={styles.tooltip}
									primaryContent={<span className={styles.text}>{altText}</span>}
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</FloatingPortal>
			)}
		</>
	);
});
